import { useEffect, useState } from "react";

/**
 * Shared aspect-ratio knowledge for uploaded media.
 *
 * Nothing in the database records a photo's dimensions, so the frame has to
 * learn them. Ratios are measured once per URL and cached for the session, so
 * a photo that has already been seen anywhere on the page renders at its real
 * shape immediately with no second layout pass.
 */
const cache = new Map<string, number>();
const inflight = new Map<string, Promise<number | null>>();

/** Feed frames for photographs: never narrower than 2:3, never wider than 1.91:1. */
export const PHOTO_MIN_RATIO = 0.72;
export const PHOTO_MAX_RATIO = 1.91;
/** Video keeps far more of its shape — vertical clips are a first-class format. */
export const VIDEO_MIN_RATIO = 0.5625;
export const VIDEO_MAX_RATIO = 2.2;

export function clampRatio(ratio: number, min: number, max: number): number {
  if (!Number.isFinite(ratio) || ratio <= 0) return min;
  return Math.min(max, Math.max(min, ratio));
}

export function cachedRatio(url: string | null | undefined): number | null {
  if (!url) return null;
  return cache.get(url) ?? null;
}

function measure(url: string): Promise<number | null> {
  const existing = inflight.get(url);
  if (existing) return existing;
  const p = new Promise<number | null>((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    const img = new window.Image();
    img.decoding = "async";
    img.onload = () => {
      const r = img.naturalWidth / img.naturalHeight;
      if (Number.isFinite(r) && r > 0) {
        cache.set(url, r);
        resolve(r);
      } else resolve(null);
    };
    img.onerror = () => resolve(null);
    img.src = url;
  }).finally(() => inflight.delete(url));
  inflight.set(url, p);
  return p;
}

/**
 * Natural width/height of an image (or a video's poster frame). Returns null
 * until it is known, which lets a frame show a calm placeholder at the fallback
 * shape instead of jumping once the file arrives.
 */
export function useNaturalRatio(url: string | null | undefined): number | null {
  const [ratio, setRatio] = useState<number | null>(() => cachedRatio(url));

  useEffect(() => {
    if (!url) { setRatio(null); return; }
    const known = cache.get(url);
    if (known) { setRatio(known); return; }
    let alive = true;
    void measure(url).then((r) => { if (alive) setRatio(r); });
    return () => { alive = false; };
  }, [url]);

  return ratio;
}

/** Remember a ratio discovered elsewhere, e.g. from a video's loadedmetadata. */
export function rememberRatio(url: string | null | undefined, ratio: number) {
  if (!url || !Number.isFinite(ratio) || ratio <= 0) return;
  cache.set(url, ratio);
}
